/* eslint-disable no-param-reassign */
/* eslint-disable import/no-cycle */
/* eslint-disable react-hooks/exhaustive-deps */

import React from 'react';
import isEqual from 'lodash/isEqual';
import { DraggedItem, DropTarget, FormItemT, FormSectionT } from '../types';
import { useSchemaNavigators } from '../useSchemaNavigators';
import { DRAG_ITEM_TYPES } from '../constants';
import { dragPreviewNode, getTargetIndicatorPath, isSectionSchema } from '../utils';
import { ComponentInputTypeT } from '../schemas/types';
import { useStoreActions, useStoreState } from '../../../../models/hooks';

interface SectionDragAndDropProps {
  itemRef: React.RefObject<HTMLDivElement>;
  path: number[];
  section: FormSectionT;
  isAdvanced?: boolean;
  optionId?: string;
  onDropItem: (dropTarget: DropTarget) => void;
}

export default function useSectionDragAndDrop({
  itemRef,
  path,
  section,
  isAdvanced,
  optionId,
  onDropItem,
}: SectionDragAndDropProps) {
  const formSchema = useStoreState((state) => state.formBuilder.formSchema);
  const draggedItem = useStoreState((state) => state.formBuilder.draggedItem);
  const dropIndicator = useStoreState((state) => state.formBuilder.dropIndicator);
  const startDrag = useStoreActions((actions) => actions.formBuilder.startDrag);
  const endDrag = useStoreActions((actions) => actions.formBuilder.endDrag);
  const addItemToSection = useStoreActions((actions) => actions.formBuilder.addItemToSection);
  const moveItemToSection = useStoreActions((actions) => actions.formBuilder.moveItemToSection);
  const setDropIndicator = useStoreActions((actions) => actions.formBuilder.setDropIndicator);

  const { determineActualParentId, findFormItemById } = useSchemaNavigators(formSchema);

  const handleDragStart = React.useCallback(
    (e: React.DragEvent) => {
      const dragPayload: DraggedItem = {
        id: section.id,
        type: DRAG_ITEM_TYPES.FORM_ITEM as 'formItem',
        itemData: { ...section },
        sourcePath: path,
      };

      startDrag(dragPayload);

      if (e.dataTransfer) {
        e.dataTransfer.setData('application/json', JSON.stringify(dragPayload));

        e.dataTransfer.effectAllowed = 'move';

        if (itemRef.current) {
          dragPreviewNode(e, itemRef.current);
        }
      }
    },
    [section, path, startDrag, itemRef],
  );

  const handleDragEnd = React.useCallback(
    (e: React.DragEvent) => {
      e.stopPropagation();
      if (
        draggedItem &&
        draggedItem.type === DRAG_ITEM_TYPES.FORM_ITEM &&
        (draggedItem.itemData as FormItemT).id === section.id
      ) {
        if (e.dataTransfer && e.dataTransfer.dropEffect === 'none') {
          endDrag();
        }
      }
    },
    [section, draggedItem, endDrag],
  );

  const handleItemDrop = React.useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();

      if (draggedItem && dropIndicator?.show && dropIndicator.parentId !== undefined) {
        if (isAdvanced && draggedItem.type === DRAG_ITEM_TYPES.PALETTE) {
          endDrag();
          return;
        }
        const actionType = draggedItem.type === DRAG_ITEM_TYPES.PALETTE ? addItemToSection : moveItemToSection;
        actionType({ draggedItem, dropTarget: dropIndicator });
        // onDropItem(dropIndicator);
      } else if (draggedItem) {
        endDrag();
      }
    },
    [draggedItem, dropIndicator, onDropItem, endDrag, addItemToSection, moveItemToSection, isAdvanced],
  );

  const handleItemDragOver = React.useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();

      const clearIndicatorAndSetNoDrop = () => {
        if (e.dataTransfer) {
          e.dataTransfer.dropEffect = 'none';
        }
        if (dropIndicator?.show || dropIndicator?.path) {
          endDrag();
        }
        e.stopPropagation();
      };

      if (!draggedItem || !itemRef.current) {
        return;
      }

      if (isAdvanced) {
        e.stopPropagation();
        return;
      }

      const { targetIndicatorPath } = getTargetIndicatorPath({ e, current: itemRef.current, path });

      const isCopy = draggedItem.type === DRAG_ITEM_TYPES.PALETTE;

      if (dropIndicator?.show && dropIndicator?.path && isEqual(dropIndicator.path, targetIndicatorPath)) {
        if (e.dataTransfer) {
          e.dataTransfer.dropEffect = isCopy ? 'copy' : 'move';
        }
        return;
      }

      if (isCopy) {
        const dragData = draggedItem.itemData as ComponentInputTypeT;
        if (dragData === 'section') {
          clearIndicatorAndSetNoDrop();
          return;
        }
      } else {
        const dragData = draggedItem.itemData as FormItemT;
        if (isSectionSchema(dragData) && !dragData.isAdvanced) {
          clearIndicatorAndSetNoDrop();
          return;
        }
      }

      if (!isCopy && (draggedItem.itemData as FormItemT).id === section.id) {
        clearIndicatorAndSetNoDrop();
        return;
      }

      const parentContainerId = determineActualParentId(path);
      const parentContainerItem = parentContainerId ? findFormItemById(parentContainerId) : null;
      const isParentContextAdvanced =
        !!parentContainerItem && isSectionSchema(parentContainerItem) && !!parentContainerItem?.isAdvanced;
      if (isParentContextAdvanced && isCopy) {
        clearIndicatorAndSetNoDrop();
        return;
      }

      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = isCopy ? 'copy' : 'move';
      }

      setDropIndicator({
        path: targetIndicatorPath,
        parentId: parentContainerId,
        isAdvancedSectionTarget: isParentContextAdvanced,
        show: true,
      });
    },
    [
      draggedItem,
      itemRef,
      path,
      dropIndicator,
      setDropIndicator,
      endDrag,
      section.id,
      isAdvanced,
      determineActualParentId,
      findFormItemById,
    ],
  );

  const handleDropInSection = React.useCallback(
    (dropTarget: DropTarget) => {
      if (!draggedItem) {
        return;
      }

      if (isAdvanced) {
        if (draggedItem.type === DRAG_ITEM_TYPES.PALETTE) {
          endDrag();
          return;
        }

        if (draggedItem.type === DRAG_ITEM_TYPES.FORM_ITEM && draggedItem.sourcePath) {
          const sourceParentId = determineActualParentId(draggedItem.sourcePath);

          if (sourceParentId === section.id) {
            endDrag();
            return;
          }
        }
      }

      const isNewItemFromPalette = draggedItem.type === DRAG_ITEM_TYPES.PALETTE;
      const actionType = isNewItemFromPalette ? addItemToSection : moveItemToSection;

      actionType({ draggedItem, dropTarget, optionId });
    },
    [
      endDrag,
      addItemToSection,
      moveItemToSection,
      draggedItem,
      isAdvanced,
      section.id,
      section.section,
      determineActualParentId,
      optionId,
    ],
  );

  return {
    handleDragStart,
    handleDragEnd,
    handleItemDrop,
    handleItemDragOver,
    handleDropInSection,
  };
}
